// Data layer for the crew-schedule grid: the schedule + active-project merge,
// survey overlay, config, permissions, section grouping, and per-date totals.
// Lifted verbatim from ManpowerPage.tsx so the page component owns only UI
// state; all derived data lives here.

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manpowerApi, surveyApi, usersApi } from '@/lib/apiClient';
import { isFinishedJobStatus } from '@/lib/startAlignment';
import {
  type ProjectRow, type Section, type ScheduledSurveyRequest,
  isSystacJobNumber, isClosedWonJobNumber, nestSubProjects, getCrewCount,
} from '../lib/scheduleModel';
import { parseHeadcount } from '../TypedHeadcountBox';

interface ApiProject {
  id: number;
  jobNumber: string;
  title: string;
  isArchived: boolean;
  projectStatus: { id: number; label: string } | null;
  division: { id: number; label: string } | null;
  currentStartDate: string | null;
  /** First day with PUBLISHED crew booked (#385) — see ProjectRow.firstCrewDate. */
  firstCrewDate?: string | null;
  isMaintenance?: boolean;
  isLoanedSpToGc?: boolean;
  loanFromDivision?: { id: number; label: string } | null;
  primaryPm?: { id: string; name: string } | null;
  bidPeakCrew?: number | null;
  bidDurationDays?: number | null;
  originalStartDate?: string | null;
  travelJob?: boolean;
}

/** How rows are ordered WITHIN a division section. Sorting is deliberately
 *  scoped to inside a section rather than across the whole grid: rows moving
 *  between sections would break the division subtotals and the headroom-vs-
 *  capacity figures those sections exist to produce. (PM request, 2026-07-25.) */
export type RowSort = 'jobNumber' | 'title' | 'pm';

export function useScheduleData(opts: {
  fromParam: string;
  toParam: string;
  dateStrs: string[];
  hiddenSections: Set<string>;
  /** Edit mode: read the draft overlay instead of the published schedule.
   *  Requires manpower.edit server-side — only pass true for editors. */
  draft?: boolean;
  /** Row order within each section. Defaults to job number. */
  sortKey?: RowSort;
  /** Show only jobs whose primary PM matches this name. Null = every PM. */
  pmFilter?: string | null;
}) {
  const {
    fromParam, toParam, dateStrs, hiddenSections, draft = false,
    sortKey = 'jobNumber', pmFilter = null,
  } = opts;

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: schedule = [], isLoading, isError, isFetching } = useQuery<ProjectRow[]>({
    queryKey: ['manpower-schedule', fromParam, toParam, { draft }],
    queryFn: () => (draft ? manpowerApi.scheduleDraft(fromParam, toParam) : manpowerApi.schedule(fromParam, toParam)),
    staleTime: draft ? 0 : 30_000,
    // Hold the previous window's rows on screen while the new one loads.
    //
    // The date range is part of the query key, so changing it lands on a key
    // with no cache — isLoading went true, the page swapped the whole <table>
    // for a "Loading schedule…" div, and the scroll container lost its height.
    // The browser then had nowhere to scroll to and reset scrollTop to 0, so a
    // scheduler who changed the range was thrown back to the top of the list
    // and had to find their job again (PM report, 2026-07-28).
    //
    // Keeping the old rows means the table never unmounts, so scroll position
    // survives. Cells for dates outside the previous window read as blank for
    // the moment the fetch takes, which is a far smaller cost than losing your
    // place — and it also removes the full-page flash on every range change.
    placeholderData: (prev) => prev,
  });

  // The Projects tab is the source of truth for "what jobs exist." We pull the
  // full non-archived list and merge it into the schedule below so that every
  // active project shows up on the grid — even if it has zero crew entries in
  // the visible window. PMs can then immediately see which jobs haven't been
  // scheduled yet (highlighted via the "Needs Scheduling" section below).
  // Complete / Canceled are filtered out client-side; we keep On Hold and
  // Pipeline / Preconstruction / Schedule Only since those are still jobs the
  // forecaster cares about.
  const {
    data: allProjects = [],
    isPending: optionsPending,
    isError: optionsError,
  } = useQuery<ApiProject[]>({
    queryKey: ['manpower-project-options', { includeArchived: false }],
    queryFn: () => manpowerApi.projectOptions(false),
    staleTime: 60_000,
    /**
     * ⚠️ Polls because the START-MISMATCH FLAG IS CROSS-ROLE (#385), which is
     * what separates this row data from the crew counts beside it.
     *
     * Invalidation is per-client. A scheduler's own edits clear their own cache,
     * so the crew numbers on this grid go stale only when somebody ELSE edits —
     * rare, because schedulers are the ones editing. The flag is not like that:
     * one of its two inputs is `current_start_date`, which a **PM** moves, on a
     * different page, in a different session. Nothing in this tab can hear that,
     * and this grid is left open all day, so without an interval the badge can
     * assert a disagreement the PM resolved hours ago.
     *
     * Five minutes, matching `useTodayData` and `LeadershipTodayPage`, whose
     * headers make the same argument for the same reason. Deliberately NOT the
     * 60s the open-request badge uses: that is one COUNT, this is the project
     * list. `refetchIntervalInBackground` stays at its default of false, so a tab
     * nobody is looking at stops polling.
     *
     * ⚠️ This is eventual consistency, not push. It bounds staleness at five
     * minutes; it does not make either screen live. Real-time would need a
     * channel this app does not have.
     */
    refetchInterval: 5 * 60 * 1000,
    // ⚠️ Shared with the Overview's Schedule & Dates card, which skips the same
    // jobs' start-mismatch banner — a finished job flags on neither screen
    // (#385, follow-up to #540). Change the rule there, not here.
    select: (d: any[]) =>
      d.filter((p: any) => !isFinishedJobStatus(p.projectStatus?.label)) as ApiProject[],
  });

  // Survey visits overlapping the visible window — keyed for cell lookup as
  // `${projectId}|${ymd}`. We pull Scheduled and In Progress in parallel; the
  // status filter is single-valued on the API, so two queries is simpler than
  // adding a multi-status filter. Anything past In Progress (Complete /
  // Canceled) is intentionally excluded so the chip drops off the grid.
  const { data: scheduledRows = [] } = useQuery<ScheduledSurveyRequest[]>({
    queryKey: ['survey-scheduled-for-manpower', fromParam, toParam],
    queryFn: () => surveyApi.listRequests({ status: 'Scheduled', from: fromParam, to: toParam }),
    staleTime: 30_000,
  });
  const { data: inProgressRows = [] } = useQuery<ScheduledSurveyRequest[]>({
    queryKey: ['survey-in-progress-for-manpower', fromParam, toParam],
    queryFn: () => surveyApi.listRequests({ status: 'In Progress', from: fromParam, to: toParam }),
    staleTime: 30_000,
  });
  const surveysByCell = useMemo(() => {
    const map = new Map<string, ScheduledSurveyRequest[]>();
    for (const r of [...scheduledRows, ...inProgressRows]) {
      if (r.projectId == null || !r.scheduledDateTime) continue;
      const ymd = r.scheduledDateTime.slice(0, 10);
      const key = `${r.projectId}|${ymd}`;
      const arr = map.get(key);
      if (arr) arr.push(r);
      else map.set(key, [r]);
    }
    return map;
  }, [scheduledRows, inProgressRows]);

  const {
    data: config = {},
    isPending: configPending,
    isError: configError,
  } = useQuery<Record<string, string | null>>({
    queryKey: ['manpower-config'],
    queryFn: manpowerApi.config,
    staleTime: 60_000,
    // On every window focus, like the Temps active box beside it (#584).
    // Available MP can be changed from another tab, device or user, and the
    // box's "More than Available MP" flag compares against this copy: with
    // only Temps active refreshing, a lowered Available MP never raised the
    // flag (Codex). 'always' because a focus inside staleTime would be skipped.
    refetchOnWindowFocus: 'always',
  });

  const availableMp = Number(config['totalAvailableMp'] ?? 144);

  /**
   * Every query the grid's FIGURES rest on has landed, and none of them failed.
   *
   * ⚠️ The schedule query alone is not enough, and the difference is invisible
   * on screen. These three are independent: the schedule can arrive while
   * project-options and config are still in flight or have failed, and each has
   * a silent fallback — `[]` for the options, `{}` for the config. A grid in
   * that state simply shows fewer rows and a different capacity, which nobody
   * reading it would question.
   *
   * It matters for the EXPORT, where the same state becomes a file that omits
   * every active job with no crew in the window, blanks every PM name, and
   * reports capacity as the hard-coded 144 fallback rather than the figure the
   * manpower lead maintains. Nothing in the workbook would say so. (Codex on #488.)
   */
  const dataReady = !isLoading && !isError
    && !optionsPending && !optionsError
    && !configPending && !configError;

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  const normalizedRoles = (me?.roles ?? []).map((r) => r.toLowerCase().replace(/\s+/g, ''));
  const permissions = me?.permissions ?? [];
  // Full editors see/edit the whole schedule; fab-scoped editors
  // (FabManpowerEditor → manpower.edit.fab) edit only Fabrication-division
  // jobs — the server enforces the scope on writes and scopes their
  // publish/reset/status/draft-overlay to fab drafts.
  const fullEdit = Boolean(
    normalizedRoles.includes('admin') ||
    normalizedRoles.includes('manpowereditor') ||
    normalizedRoles.includes('manpower') ||
    permissions.includes('manpower.edit')
  );
  const fabEditOnly = !fullEdit && permissions.includes('manpower.edit.fab');
  const canEdit = fullEdit || fabEditOnly;
  // Bulk schedule operations (Excel import, Sync Projects) are Admin-only by
  // policy (product owner, 2026-07-29) — they rewrite or clear whole swathes of the
  // schedule, so they sit above ordinary manpower.edit.
  const isAdmin = normalizedRoles.includes('admin');

  // ── Derived data ───────────────────────────────────────────────────────────

  // Merge the schedule (which only includes projects with crew entries in the
  // window) with the full active-project list so every job from the Projects
  // tab appears on the grid. Projects without entries get an empty
  // dailyEntries[] and synthesized metadata (isSystac/isUnmatchedImport
  // default to false; division comes from the project record).
  const mergedRows = useMemo<ProjectRow[]>(() => {
    const startDateByProject = new Map(allProjects.map((p) => [p.id, p.currentStartDate]));
    // #385: the Scheduler's answer to the same question the PM's start date
    // answers. Server-derived and window-independent -- see ProjectRow.firstCrewDate.
    //
    // ⚠️ A FINISHED JOB GETS NEITHER DATE, AND THAT IS THE INTENT rather than an
    // accident of the lookup. `allProjects` is projectOptions(false) with the
    // Complete/Canceled filter applied above, while `getSchedule` returns a row
    // for ANY project with entries in the window (it filters `deleted_at` only).
    // So an archived or finished job that still carries booked crew renders here
    // and misses both maps — no planned start, no first crew day, and therefore
    // no Start ≠ Crew badge.
    //
    // Keeping it that way is deliberate. The badge exists to start a conversation
    // between a PM and a Scheduler about a job's start; nobody is going to
    // re-plan the start of a job that is Complete, and on prod these finished
    // rows are a large share of the disagreements — flagging them would spend the
    // feature's credibility on work nobody can act on. Stale crew on a finished
    // job is a real problem, but it already has an owner: the Sync tool, which
    // exists to find and clear exactly those entries.
    //
    // ⚠️ The enrichment is therefore the wrong place to "fix" this. Widening it
    // to the unfiltered options would light the badge on every finished job with
    // a stale booking. If that is ever wanted, it should be a deliberate choice
    // with its own copy, not a side effect of a map lookup.
    const firstCrewByProject = new Map(allProjects.map((p) => [p.id, p.firstCrewDate ?? null]));
    // The schedule feed doesn't carry the PM, so merge it in from
    // project-options the same way the start date is.
    const pmByProject = new Map(allProjects.map((p) => [p.id, p.primaryPm?.name ?? null]));
    // Same story for the bid forecast (#330): the schedule feed is built from
    // mp_schedule_entries, which a SYSTAC pursuit typically has none of.
    const bidByProject = new Map(allProjects.map((p) =>
      [p.id, {
        crew: p.bidPeakCrew ?? null,
        days: p.bidDurationDays ?? null,
        origStart: p.originalStartDate ?? null,
      }]));
    const out: ProjectRow[] = [];
    const seen = new Set<number>();

    // Schedule rows first so we preserve any server-side fields
    // (isUnmatchedImport, etc.) that aren't on the bare project record.
    // SYSTAC and ClosedWon status are derived from the current job-number
    // prefix — not the sticky server flags — so awarded jobs with a real
    // number drop the grouping/badge (see isSystacJobNumber /
    // isClosedWonJobNumber).
    for (const r of schedule) {
      out.push({
        ...r,
        isSystac: isSystacJobNumber(r.jobNumber),
        isClosedWonPlaceholder: isClosedWonJobNumber(r.jobNumber),
        currentStartDate: startDateByProject.get(r.projectId) ?? null,
        firstCrewDate: firstCrewByProject.get(r.projectId) ?? null,
        pm: pmByProject.get(r.projectId) ?? null,
        bidPeakCrew: bidByProject.get(r.projectId)?.crew ?? null,
        bidDurationDays: bidByProject.get(r.projectId)?.days ?? null,
        originalStartDate: bidByProject.get(r.projectId)?.origStart ?? null,
      });
      seen.add(r.projectId);
    }
    // Then any active project that didn't appear in the schedule window.
    for (const p of allProjects) {
      if (seen.has(p.id)) continue;
      out.push({
        projectId: p.id,
        jobNumber: p.jobNumber,
        title: p.title,
        isSystac: isSystacJobNumber(p.jobNumber),
        isClosedWonPlaceholder: isClosedWonJobNumber(p.jobNumber),
        isUnmatchedImport: false,
        isMaintenance: p.isMaintenance ?? false,
        parentProjectId: null,
        isLoanedSpToGc: p.isLoanedSpToGc ?? false,
        loanFromDivision: p.loanFromDivision ?? null,
        division: p.division ?? null,
        // ⚠️ Carried, not defaulted to false. These are the rows with no crew
        // in the window — the whole "Needs Scheduling" section — and leaving
        // it off made every travel job among them render as Local.
        travelJob: p.travelJob ?? false,
        dailyEntries: [],
        currentStartDate: p.currentStartDate,
        firstCrewDate: p.firstCrewDate ?? null,
        pm: p.primaryPm?.name ?? null,
        bidPeakCrew: p.bidPeakCrew ?? null,
        bidDurationDays: p.bidDurationDays ?? null,
        originalStartDate: p.originalStartDate ?? null,
      });
    }
    return out;
  }, [schedule, allProjects]);

  // Maintenance parents are header rows: their day cells show the read-only sum
  // of their sub-projects' crew rather than an editable count of their own.
  const childrenByParentId = useMemo(() => {
    const m = new Map<number, ProjectRow[]>();
    for (const r of mergedRows) {
      if (r.parentProjectId != null) {
        const arr = m.get(r.parentProjectId) ?? [];
        arr.push(r);
        m.set(r.parentProjectId, arr);
      }
    }
    return m;
  }, [mergedRows]);

  const maintenanceDayTotal = useCallback((parentId: number, ds: string): number =>
    (childrenByParentId.get(parentId) ?? []).reduce((sum, child) => sum + getCrewCount(child, ds), 0),
  [childrenByParentId]);

  // Group projects into sections: a synthetic "Needs Scheduling" section
  // surfaces urgent rows at the very top; ClosedWon placeholders get their own
  // group; everything else — SYSTAC included — is grouped by division (with
  // "No Division" as a fallback bucket). SYSTAC rows keep their per-row tint
  // and badge, and the standalone SYSTAC chip filters them across divisions.
  const sections = useMemo<Section[]>(() => {
    /**
     * "Needs scheduling" rule: the project's currentStartDate falls inside the
     * visible window AND there are no positive crew entries for the project in
     * that window. These rows float to the top in a synthetic section so PMs
     * immediately see which newly-added projects still need crews booked.
     */
    function isNeedsScheduling(p: ProjectRow): boolean {
      // Maintenance parents and their schedule-only sub-projects are persistent
      // rows — they never belong in the "Needs Scheduling" urgency bucket.
      if (p.isMaintenance || p.parentProjectId != null) return false;
      if (!p.currentStartDate) return false;
      const startYmd = p.currentStartDate.slice(0, 10);
      const windowStart = dateStrs[0];
      const windowEnd   = dateStrs[dateStrs.length - 1];
      if (startYmd < windowStart || startYmd > windowEnd) return false;
      // Grouping anchors to PUBLISHED crew, not staged drafts — otherwise the
      // row jumps out of the Needs Scheduling section (and out of view) on the
      // scheduler's first staged edit. It relocates to its division section
      // when the draft is published (the refetched published data then has
      // crew). Draft-overlaid entries carry the published value alongside.
      return !p.dailyEntries.some((e) => (e.isDraft ? (e.publishedCrewCount ?? 0) : e.crewCount) > 0);
    }

    const groups = new Map<string, Section>();
    const NEEDS_KEY = 'needs-scheduling';

    // PM filter. Maintenance sub-projects carry no PM of their own, so a naive
    // filter would strand them away from their parent — keep a sub whenever its
    // parent survives the filter.
    let visibleRows = mergedRows;
    if (pmFilter) {
      const keptIds = new Set(
        mergedRows.filter((p) => (p.pm ?? '') === pmFilter).map((p) => p.projectId),
      );
      visibleRows = mergedRows.filter(
        (p) => keptIds.has(p.projectId)
          || (p.parentProjectId != null && keptIds.has(p.parentProjectId)),
      );
    }

    for (const p of visibleRows) {
      const needs = isNeedsScheduling(p);
      const key   = needs
        ? NEEDS_KEY
        : p.isClosedWonPlaceholder ? 'closed-won'
        : p.division ? `div-${p.division.id}`
        : 'div-none';
      const label = needs
        ? '⚠ Needs Scheduling'
        : p.isClosedWonPlaceholder ? 'ClosedWon'
        : p.division ? p.division.label
        : 'No Division';
      const existing = groups.get(key);
      if (existing) existing.projects.push(p);
      else groups.set(key, {
        key, label,
        isClosedWonPlaceholder: !needs && p.isClosedWonPlaceholder,
        divisionId:             needs ? null : (p.division?.id ?? null),
        isNeedsScheduling:      needs,
        projects:               [p],
      });
    }
    // Order rows inside each section, THEN nest — nestSubProjects preserves the
    // parent ordering it is handed and simply tucks children underneath, so
    // sorting first keeps maintenance groups intact.
    const collate = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    const rowKey = (p: ProjectRow): string =>
      sortKey === 'title' ? p.title
        : sortKey === 'pm' ? (p.pm ?? '')
        : p.jobNumber;
    for (const s of groups.values()) {
      s.projects = nestSubProjects(
        [...s.projects].sort((a, b) => {
          const av = rowKey(a);
          const bv = rowKey(b);
          // Rows missing the sort value (no PM set) sink to the bottom rather
          // than heading the list, which is what an empty string would do.
          if (!av !== !bv) return av ? -1 : 1;
          const primary = collate(av, bv);
          // Job number is the tie-break everywhere, so equal PMs or duplicate
          // titles still come back in a stable, predictable order.
          return primary !== 0 ? primary : collate(a.jobNumber, b.jobNumber);
        }),
      );
    }

    return [...groups.values()].sort((a, b) => {
      // Needs Scheduling always first.
      if (a.isNeedsScheduling      !== b.isNeedsScheduling)      return a.isNeedsScheduling ? -1 : 1;
      // ClosedWon last; real divisions first, then No Division.
      if (a.isClosedWonPlaceholder !== b.isClosedWonPlaceholder) return a.isClosedWonPlaceholder ? 1 : -1;
      if ((a.divisionId === null)  !== (b.divisionId === null))
                                                                 return a.divisionId === null ? 1 : -1;
      if (a.divisionId !== null && b.divisionId !== null)
                                                                 return a.divisionId - b.divisionId;
      return 0;
    });
  }, [mergedRows, dateStrs, sortKey, pmFilter]);

  // The 'systac' key in hiddenSections is a cross-division row filter, not a
  // section: hiding it drops SYSTAC rows from every section they appear in.
  const systacCount  = useMemo(() => mergedRows.filter((p) => p.isSystac).length, [mergedRows]);
  const systacHidden = hiddenSections.has('systac');

  const visibleSections = useMemo(
    () => sections
      .filter((s) => !hiddenSections.has(s.key))
      .map((s) => (systacHidden ? { ...s, projects: s.projects.filter((p) => !p.isSystac) } : s))
      .filter((s) => s.projects.length > 0),
    [sections, hiddenSections, systacHidden],
  );

  // Flat list of visible projects, used by batch-edit + column-total logic.
  const rows = useMemo<ProjectRow[]>(
    () => visibleSections.flatMap((s) => s.projects),
    [visibleSections],
  );

  // NOTE: the subtotal / grand-total arrays used to be computed here too, but
  // nothing consumed them — the page recomputes both off its own search- and
  // scope-filtered sections. Two copies of the same sum is how a totals rule
  // drifts, so the dead pair is gone; ManpowerPage owns them.

  // Division options for the "loaned from" picker — derived from the projects
  // already in view so we never hard-code division ids.
  const divisionOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of allProjects) if (p.division) m.set(p.division.id, p.division.label);
    for (const r of schedule) if (r.division) m.set(r.division.id, r.division.label);
    return [...m.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allProjects, schedule]);

  // Every PM with at least one job on the grid, for the filter dropdown.
  // Derived from the unfiltered rows so the current selection never removes
  // itself from the list.
  const pmOptions = useMemo(
    () => [...new Set(mergedRows.map((p) => p.pm).filter((n): n is string => !!n))]
      .sort((a, b) => a.localeCompare(b)),
    [mergedRows],
  );

  return {
    isLoading, isError, dataReady,
    // True while a new window is in flight with previous rows still on screen —
    // lets the page show a quiet indicator instead of unmounting the grid.
    isFetching,
    surveysByCell,
    availableMp,
    // Whether availableMp is a figure someone entered, rather than the
    // hard-coded 144 fallback (#584). Not while the config is in flight or
    // failed, and not when it loaded without a valid stored row: setConfig
    // compares temps only against a parseable stored value, so judging them
    // against 144 here would refuse and flag counts the API accepts.
    availableMpKnown: !configPending && !configError
      && parseHeadcount(config['totalAvailableMp']) !== null,
    config,
    canEdit,
    fabEditOnly,
    isAdmin,
    mergedRows,
    maintenanceDayTotal,
    sections,
    visibleSections,
    // Exposed so the page can tell which subs are on the grid and which the
    // parent still has to speak for in the totals (see getTotalledCrewCount).
    childrenByParentId,
    rows,
    systacCount,
    systacHidden,
    divisionOptions,
    pmOptions,
  };
}
